import { useState, useEffect } from 'react';
import { UnifiedKeypoints } from '@exerai/vision-engine-web';
import { SideOfBody } from '@/common/const';
import { Nullable, Nullish, SideGaitResultsInsights } from '@/common/types';

interface UseResultsInsights {
    lap1CycleStartX: Nullish<number>;
    lap1StepStartX: Nullish<number>;
    lap1CycleEndX: Nullish<number>;
    lap2CycleStartX: Nullish<number>;
    lap2StepStartX: Nullish<number>;
    lap2CycleEndX: Nullish<number>;
    leftCycleStartFrame: Nullish<number>;
    leftStepStartFrame: Nullish<number>;
    leftCycleEndFrame: Nullish<number>;
    rightCycleStartFrame: Nullish<number>;
    rightStepStartFrame: Nullish<number>;
    rightCycleEndFrame: Nullish<number>;
}

export const useResultsInsights = (
    resultsInsights: SideGaitResultsInsights,
    poseLog: UnifiedKeypoints[],
): UseResultsInsights => {
    const [lap1CycleStartX, setLap1CycleStartX] = useState<Nullish<number>>();
    const [lap1StepStartX, setLap1StepStartX] = useState<Nullish<number>>();
    const [lap1CycleEndX, setLap1CycleEndX] = useState<Nullish<number>>();
    const [lap2CycleStartX, setLap2CycleStartX] = useState<Nullish<number>>();
    const [lap2StepStartX, setLap2StepStartX] = useState<Nullish<number>>();
    const [lap2CycleEndX, setLap2CycleEndX] = useState<Nullish<number>>();
    const [leftCycleStartFrame, setLeftCycleStartFrame] = useState<Nullish<number>>();
    const [leftStepStartFrame, setLeftStepStartFrame] = useState<Nullish<number>>();
    const [leftCycleEndFrame, setLeftCycleEndFrame] = useState<Nullish<number>>();
    const [rightCycleStartFrame, setRightCycleStartFrame] = useState<Nullish<number>>();
    const [rightStepStartFrame, setRightStepStartFrame] = useState<Nullish<number>>();
    const [rightCycleEndFrame, setRightCycleEndFrame] = useState<Nullish<number>>();

    useEffect(() => {
        const { lapInfo } = resultsInsights;
        if (!lapInfo) {
            return;
        }
        let firstLapSide: SideOfBody;
        let leftLapStartFrame: number;
        let rightLapStartFrame: number;
        let leftCycleStartX: Nullable<number>;
        let leftStepStartX: Nullable<number> = null;
        let leftCycleEndX: Nullable<number>;
        let rightCycleStartX: Nullable<number>;
        let rightStepStartX: Nullable<number> = null;
        let rightCycleEndX: Nullable<number>;
        const leftCycleStartFrameRelative = resultsInsights.strideInfo?.left?.bestCycle.startFrame;
        const rightCycleStartFrameRelative = resultsInsights.strideInfo?.right?.bestCycle.startFrame;
        const leftCycleEndFrameRelative = resultsInsights.strideInfo?.left?.bestCycle.endFrame;
        const rightCycleEndFrameRelative = resultsInsights.strideInfo?.right?.bestCycle.endFrame;
        if (
            leftCycleStartFrameRelative &&
            rightCycleStartFrameRelative &&
            leftCycleEndFrameRelative &&
            rightCycleEndFrameRelative &&
            resultsInsights.resultFrames?.['v3.1.0|min-right-knee-flexion|MIN_ANGLE']
        ) {
            // Hack for now to determine first lap side
            if (
                resultsInsights.resultFrames['v3.1.0|min-right-knee-flexion|MIN_ANGLE'] <
                resultsInsights.resultFrames['v3.1.0|min-left-knee-flexion|MIN_ANGLE']
            ) {
                firstLapSide = SideOfBody.RIGHT;
                rightLapStartFrame = lapInfo.lap1StartFrame;
                leftLapStartFrame = lapInfo.lap2StartFrame;
            } else {
                firstLapSide = SideOfBody.LEFT;
                rightLapStartFrame = lapInfo.lap2StartFrame;
                leftLapStartFrame = lapInfo.lap1StartFrame;
            }

            setLeftCycleStartFrame(leftLapStartFrame + leftCycleStartFrameRelative);
            if (resultsInsights.strideInfo?.left?.stepInfo?.frameLength) {
                setLeftStepStartFrame(
                    leftLapStartFrame +
                    (leftCycleEndFrameRelative - resultsInsights.strideInfo.left.stepInfo.frameLength),
                );
            }

            setLeftCycleEndFrame(leftLapStartFrame + leftCycleEndFrameRelative);
            setRightCycleStartFrame(rightLapStartFrame + rightCycleStartFrameRelative);
            if (resultsInsights.strideInfo?.right?.stepInfo?.frameLength) {
                setRightStepStartFrame(
                    rightLapStartFrame +
                    (rightCycleEndFrameRelative - resultsInsights.strideInfo.right.stepInfo.frameLength),
                );
            }
            setRightCycleEndFrame(rightLapStartFrame + rightCycleEndFrameRelative);

            leftCycleStartX =
                poseLog[leftLapStartFrame + leftCycleStartFrameRelative].HEEL_LEFT.coordinates.x > 0
                    ? poseLog[leftLapStartFrame + leftCycleStartFrameRelative].HEEL_LEFT.coordinates.x
                    : null;

            if (resultsInsights.strideInfo?.left?.stepInfo?.frameLength) {
                // Opposite Heel
                leftStepStartX =
                    poseLog[
                        leftLapStartFrame +
                        (leftCycleEndFrameRelative - resultsInsights.strideInfo.left.stepInfo.frameLength)
                    ].HEEL_RIGHT.coordinates.x > 0
                        ? poseLog[
                            leftLapStartFrame +
                            (leftCycleEndFrameRelative - resultsInsights.strideInfo.left.stepInfo.frameLength)
                        ].HEEL_RIGHT.coordinates.x
                        : null;
            }

            leftCycleEndX =
                poseLog[leftLapStartFrame + leftCycleEndFrameRelative].HEEL_LEFT.coordinates.x > 0
                    ? poseLog[leftLapStartFrame + leftCycleEndFrameRelative].HEEL_LEFT.coordinates.x
                    : null;

            rightCycleStartX =
                poseLog[rightLapStartFrame + rightCycleStartFrameRelative].HEEL_RIGHT.coordinates.x > 0
                    ? poseLog[rightLapStartFrame + rightCycleStartFrameRelative].HEEL_RIGHT.coordinates.x
                    : null;

            if (resultsInsights.strideInfo?.right?.stepInfo?.frameLength) {
                // Opposite Heel
                rightStepStartX =
                    poseLog[
                        rightLapStartFrame +
                        (rightCycleEndFrameRelative - resultsInsights.strideInfo.right.stepInfo.frameLength)
                    ].HEEL_LEFT.coordinates.x > 0
                        ? poseLog[
                            rightLapStartFrame +
                            (rightCycleEndFrameRelative - resultsInsights.strideInfo.right.stepInfo.frameLength)
                        ].HEEL_LEFT.coordinates.x
                        : null;
            }

            rightCycleEndX =
                poseLog[rightLapStartFrame + rightCycleEndFrameRelative].HEEL_RIGHT.coordinates.x > 0
                    ? poseLog[rightLapStartFrame + rightCycleEndFrameRelative].HEEL_RIGHT.coordinates.x
                    : null;

            if (firstLapSide == SideOfBody.RIGHT) {
                setLap1CycleStartX(rightCycleStartX);
                if (rightStepStartX) {
                    setLap1StepStartX(rightStepStartX);
                }
                setLap1CycleEndX(rightCycleEndX);
                setLap2CycleStartX(leftCycleStartX);
                if (leftStepStartX) {
                    setLap2StepStartX(leftStepStartX);
                }
                setLap2CycleEndX(leftCycleEndX);
            } else {
                setLap1CycleStartX(leftCycleStartX);
                if (leftStepStartX) {
                    setLap1StepStartX(leftStepStartX);
                }
                setLap1CycleEndX(leftCycleEndX);
                setLap2CycleStartX(rightCycleStartX);
                if (rightStepStartX) {
                    setLap2StepStartX(rightStepStartX);
                }
                setLap2CycleEndX(rightCycleEndX);
            }
        }
    }, [poseLog, resultsInsights]);

    return {
        lap1CycleStartX,
        lap1StepStartX: lap1StepStartX,
        lap1CycleEndX,
        lap2CycleStartX,
        lap2StepStartX: lap2StepStartX,
        lap2CycleEndX,
        leftCycleStartFrame,
        leftStepStartFrame: leftStepStartFrame,
        leftCycleEndFrame,
        rightCycleStartFrame,
        rightStepStartFrame: rightStepStartFrame,
        rightCycleEndFrame,
    };
};
